"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CropEditor = void 0;
var React = __importStar(require("react"));
var cropro_1 = require("cropro");
var toCropAreaState_1 = require("./toCropAreaState");
var react_1 = require("@chakra-ui/react");
var sharedStyles_1 = require("./sharedStyles");
var MarkupImage_1 = require("./MarkupImage");
var usePaddedImageDetails_1 = require("./usePaddedImageDetails");
var MediaContext_1 = require("../../MediaContext");
// Enable CROPRO license. Removes the logo from the editor.
// This key is already exposed since we have to set this on the frontend. It's low risk so we
// hardcode it for simplicity.
cropro_1.Activator.addKey('CRPR-F992-S846-6443');
var mediaRatioToCropAreaRatio = {
    FOUR_THREE: { horizontal: 4, vertical: 3 },
    ONE_ONE: { horizontal: 1, vertical: 1 },
};
function CropEditor(_a) {
    var originalImageInfo = _a.imageInfo, sourceImage = _a.sourceImage, onClose = _a.onClose, onRender = _a.onRender;
    var _js = (0, MediaContext_1.useMediaContext)()._js;
    var toast = (0, react_1.useToast)();
    var tooSmallToastIdRef = React.useRef();
    React.useEffect(function () {
        // This will override the css variable that gets set by the toast component.
        document.documentElement.style.setProperty('--toast-z-index', (Number(sharedStyles_1.editorZIndex) + 1).toString(10));
        return function () {
            // Remove the override when this component unmounts
            document.documentElement.style.removeProperty('--toast-z-index');
        };
    }, []);
    var _b = React.useState(originalImageInfo), imageInfo = _b[0], setImageInfo = _b[1];
    var _c = (0, usePaddedImageDetails_1.usePaddedImageDetails)(imageInfo, sourceImage), shouldUsePaddedImage = _c.shouldUsePaddedImage, paddedImageInfo = _c.paddedImageInfo, paddedImageSrc = _c.paddedImageSrc, paddedCropAreaState = _c.paddedCropAreaState, convertPaddedStateToSourceState = _c.convertPaddedStateToSourceState;
    var _d = React.useState(null), paddedImage = _d[0], setPaddedImage = _d[1];
    var alreadyClosingDueToCleanup = React.useRef(false);
    var memoizedOnClose = React.useCallback(onClose, []);
    var memoizedOnRender = React.useCallback(onRender, []);
    var memoizedConvertPaddedStateToSourceState = React.useCallback(convertPaddedStateToSourceState, [convertPaddedStateToSourceState]);
    React.useEffect(function () {
        var _a, _b, _c, _d;
        alreadyClosingDueToCleanup.current = false;
        if (!sourceImage || (shouldUsePaddedImage && !paddedImage)) {
            return;
        }
        var aspectRatioToastId;
        var previousAnnotationsToastId;
        var cropArea = new cropro_1.CropArea(paddedImage || sourceImage);
        var previousState = paddedCropAreaState || (0, toCropAreaState_1.toCropAreaState)(imageInfo);
        cropArea.renderAtNaturalSize = true;
        if (imageInfo.encoding === 'jpeg') {
            cropArea.renderImageType = 'image/jpeg';
            // 100% image quality on huge images causes problems. Lowering it slightly seems to fix it.
            cropArea.renderImageQuality = 0.9;
        }
        cropArea.zoomToCropEnabled = false;
        cropArea.displayMode = 'popup';
        var filterRatio = (_a = imageInfo.filterInfo) === null || _a === void 0 ? void 0 : _a.ratio;
        var requiredAspectRatio = filterRatio ? mediaRatioToCropAreaRatio[filterRatio] : null;
        if (requiredAspectRatio) {
            cropArea.aspectRatios = [requiredAspectRatio];
            if (imageInfo.ratio !== filterRatio &&
                (((_b = imageInfo.markupState) === null || _b === void 0 ? void 0 : _b.edits) || ((_c = imageInfo.markupState) === null || _c === void 0 ? void 0 : _c.annotations))) {
                aspectRatioToastId = toast({
                    id: 'image-editor-aspect-ratio-warning',
                    title: _js('A different aspect ratio is required for this image.'),
                    description: _js('This image was previously edited but now requires a different aspect ratio. Please save the image with the suggested crop, or adjust and save to proceed. Otherwise, exit the editor to cancel.'),
                    status: 'warning',
                    isClosable: true,
                    duration: 20000,
                    position: 'top',
                });
                // If we need to use a padded image we'll already have the paddedCropAreaState calculated.
                if (!shouldUsePaddedImage) {
                    // We don't want to restore state with a crop at the wrong aspect ratio, so we ignore
                    // any previous state. This effectively lets the user start over from the source image
                    // with the crop aspect ratio fixed to what is required and a suggested crop.
                    previousState = (0, toCropAreaState_1.convertSuggestedCropToCropAreaState)(imageInfo);
                }
            }
        }
        // The media manager modal has a large z-index, so we need an even bigger one when opening
        // this editor from an image in the media manager :/
        cropArea.styles.settings.zIndex = sharedStyles_1.editorZIndex;
        // Help the crop frame stand out from white padding that may be present.
        cropArea.styles.settings.cropFrameColor = 'gray.200';
        cropArea.addCloseEventListener(function () {
            // Normally, this runs first, then cleanup.
            // However, if cleanup runs outside of component unmount (such as in React Strict Mode),
            //  a "cleanup" close can trigger this, which results in the markup editor *actually* closing,
            //  usually right after it opens.
            if (!alreadyClosingDueToCleanup.current) {
                memoizedOnClose();
                setPaddedImage(null);
                toast.close(tooSmallToastIdRef.current);
            }
        });
        cropArea.addRenderEventListener(function (dataUrl, state) {
            toast.close(tooSmallToastIdRef.current);
            if (state === undefined) {
                return;
            }
            var data = memoizedConvertPaddedStateToSourceState(state);
            var _a = cropIsTooSmall(data, imageInfo), isTooSmall = _a.isTooSmall, minWidth = _a.minWidth, minHeight = _a.minHeight;
            if (isTooSmall) {
                tooSmallToastIdRef.current = toast({
                    title: _js('Cannot save image edits!'),
                    description: _js('The chosen crop is too small. Please adjust the crop and try again. The image should have a minimum width of %1 pixels and a minimum height of %2 pixels.', minWidth === null || minWidth === void 0 ? void 0 : minWidth.toString(10), minHeight === null || minHeight === void 0 ? void 0 : minHeight.toString(10)),
                    status: 'error',
                    isClosable: true,
                    duration: 20000,
                    position: 'top',
                });
                // Reset the image info with the latest edits state so the editor opens again.
                // Unfortunately, CROPRO doesn't support a way to natively limit the crop rect size or
                // disable the save button. So the best we can do is reopening the editor after the user
                // attempts to save.
                setImageInfo(__assign(__assign({}, imageInfo), { markupState: {
                        edits: {
                            type: 'CROPRO',
                            version: '1',
                            data: data,
                        },
                    } }));
                return;
            }
            setPaddedImage(null);
            memoizedOnRender(dataUrl, {
                type: 'CROPRO',
                version: '1',
                data: data,
            });
        });
        cropArea.show();
        if (previousState) {
            cropArea.restoreState(previousState);
        }
        if ((_d = imageInfo.markupState) === null || _d === void 0 ? void 0 : _d.annotations) {
            previousAnnotationsToastId = toast({
                title: _js('Editing this image will remove any existing markers.'),
                description: _js('Markers cannot be copied to the newly edited image and must be manually reapplied after your changes are saved. You can exit this editor without saving to cancel.'),
                status: 'info',
                isClosable: true,
                duration: 20000,
                position: 'top',
            });
        }
        return function () {
            alreadyClosingDueToCleanup.current = true;
            cropArea.close();
            toast.close(aspectRatioToastId);
            toast.close(previousAnnotationsToastId);
        };
    }, [
        sourceImage,
        imageInfo,
        memoizedOnClose,
        memoizedOnRender,
        toast,
        shouldUsePaddedImage,
        paddedImage,
        paddedImageInfo,
        paddedCropAreaState,
        memoizedConvertPaddedStateToSourceState,
    ]);
    return (React.createElement(MarkupImage_1.MarkupImage, { imageInfo: paddedImageInfo, imageSrc: paddedImageSrc, onLoad: function (image) { return setPaddedImage(image); } }));
}
exports.CropEditor = CropEditor;
function cropIsTooSmall(cropAreaState, imageInfo) {
    var sourceImageInfo = imageInfo.srcImageInfo || imageInfo;
    var filterInfo = imageInfo.filterInfo;
    var scale = sourceImageInfo.width / cropAreaState.width;
    var cropWidth = scale * cropAreaState.cropRect.width;
    var cropHeight = scale * cropAreaState.cropRect.height;
    var minWidth = (filterInfo === null || filterInfo === void 0 ? void 0 : filterInfo.width) ? Number(filterInfo.width) : null;
    var minHeight = (filterInfo === null || filterInfo === void 0 ? void 0 : filterInfo.height) ? Number(filterInfo.height) : null;
    return {
        isTooSmall: (minWidth && cropWidth < minWidth) || (minHeight && cropHeight < minHeight),
        minWidth: minWidth,
        minHeight: minHeight,
    };
}
